import { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/common/Layout/Layout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageBody, PageHeader, PageShell } from '@/components/ui/page-shell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Plus,
  FileText,
  CreditCard,
  Receipt,
  AlertTriangle,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { UnifiedInvoiceAPI } from '@/services/invoicing';
import { useStore } from '@/lib/store';
import { useAuth } from '@/hooks/useAuth';
import { InvoiceDetails } from '@/components/invoices/InvoiceDetails';
import api from '@/lib/api';
import {
  UnifiedInvoice,
  UnifiedInvoiceFilters,
} from '@/types/invoicing';
import {
  SendEmailDialog,
  BulkEmailDialog,
  MergeInvoicesDialog,
  CreateInvoiceDialog,
  EditInvoiceDialog,
  ApplyToInvoiceDialog,
  InvoiceFilters,
  InvoiceTable,
} from '@/components/invoices/unified-invoicing';

interface InvoiceTotals {
  total_sum: number;
  subtotal_sum?: number;
  paid_sum?: number;
  vat_amount_sum: number;
  service_charge_sum: number;
  costs: number;
  count: number;
  legal_fees?: number;
}

// Helper function to calculate financial year dates based on firm's financial year start date
function getFinancialYearDates(financialYearStartDate: string | undefined): { start: Date; end: Date } {
  const today = new Date();
  const currentYear = today.getFullYear();
  
  // Default to January 1 if no financial year start date is set
  if (!financialYearStartDate) {
    return {
      start: new Date(currentYear, 0, 1),
      end: new Date(currentYear, 11, 31),
    };
  }
  
  // Parse the financial year start date (format: "YYYY-MM-DD")
  const [_year, month, day] = financialYearStartDate.split('-').map(Number);
  
  // Create financial year start date for current year
  let financialYearStart = new Date(currentYear, month - 1, day);
  
  // If today is before the financial year start of current year,
  // the current financial year started in the previous calendar year
  if (today < financialYearStart) {
    financialYearStart = new Date(currentYear - 1, month - 1, day);
  }
  
  // Financial year end is one day before the next financial year start
  const financialYearEnd = new Date(financialYearStart);
  financialYearEnd.setFullYear(financialYearEnd.getFullYear() + 1);
  financialYearEnd.setDate(financialYearEnd.getDate() - 1);
  
  return {
    start: financialYearStart,
    end: financialYearEnd,
  };
}

export function UnifiedInvoicing() {
  const { clients, matters, fetchAll } = useStore();
  const { user } = useAuth();

  // Firm settings state
  const [firmSettings, setFirmSettings] = useState<{ financial_year_start_date?: string; default_invoice_description?: string } | null>(null);
  const [firmSettingsLoaded, setFirmSettingsLoaded] = useState(false);

  // Calculate financial year dates based on firm settings
  const { start: financialYearStart, end: financialYearEnd } = useMemo(() => {
    return getFinancialYearDates(firmSettings?.financial_year_start_date);
  }, [firmSettings?.financial_year_start_date]);

  // State
  const [invoices, setInvoices] = useState<UnifiedInvoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totals, setTotals] = useState<InvoiceTotals>({
    total_sum: 0,
    paid_sum: 0,
    vat_amount_sum: 0,
    service_charge_sum: 0,
    costs: 0,
    count: 0,
  });

  // Filters - will be updated once firm settings are loaded
  const [filters, setFilters] = useState<UnifiedInvoiceFilters>({
    type: undefined,
    clientId: undefined,
    matterId: undefined,
    dateFrom: undefined, // Will be set after firm settings are loaded
    dateTo: undefined,   // Will be set after firm settings are loaded
    status: undefined,
  });

  // Column filters (from table headers)
  const [columnFilters, setColumnFilters] = useState<{
    types: string[];
    clients: number[];
    matters: number[];
    statuses: string[];
  }>({
    types: [],
    clients: [],
    matters: [],
    statuses: [],
  });

  // Fetch firm settings to get financial year start date
  const fetchFirmSettings = useCallback(async () => {
    const firmId = user?.firms?.[0];
    if (!firmId) {
      // No firm ID available, use default calendar year
      setFirmSettingsLoaded(true);
      return;
    }

    try {
      const response = await api.get(`/api/firms/${firmId}/`);
      setFirmSettings({
        financial_year_start_date: response.data.financial_year_start_date,
        default_invoice_description: response.data.default_invoice_description,
      });
    } catch (error) {
      console.error('Failed to fetch firm settings:', error);
      // On error, we'll use default calendar year dates
    } finally {
      setFirmSettingsLoaded(true);
    }
  }, [user?.firms]);

  // Update filters when firm settings are loaded
  useEffect(() => {
    if (firmSettingsLoaded) {
      setFilters(prev => ({
        ...prev,
        dateFrom: financialYearStart,
        dateTo: financialYearEnd,
      }));
    }
  }, [firmSettingsLoaded, financialYearStart, financialYearEnd]);

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createType, setCreateType] = useState<'invoice' | 'credit_note' | 'downpayment'>('invoice');
  const [selectedInvoice, setSelectedInvoice] = useState<UnifiedInvoice | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);

  // Bulk operations
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);

  // Create client and matter maps
  const clientMap = useMemo(() => {
    const map: Record<number, string> = {};
    clients.forEach((client) => {
      map[client.id] = client.name;
    });
    return map;
  }, [clients]);

  const matterMap = useMemo(() => {
    const map: Record<number, string> = {};
    matters.forEach((matter) => {
      map[matter.id] = matter.group_name ? `${matter.group_name} - ${matter.name}` : matter.name;
    });
    return map;
  }, [matters]);

  // Load initial data - fetch firm settings first
  useEffect(() => {
    fetchFirmSettings();
    fetchAll();
  }, [fetchFirmSettings]);

  // Fetch invoices only after firm settings are loaded (so we have correct date filters)
  useEffect(() => {
    if (firmSettingsLoaded && filters.dateFrom && filters.dateTo) {
      fetchInvoices();
      fetchTotals();
    }
  }, [firmSettingsLoaded]);

  // Refetch when page or filters change
  useEffect(() => {
    if (firmSettingsLoaded && filters.dateFrom && filters.dateTo) {
      fetchInvoices();
      fetchTotals();
      // Clear selections when data changes
      setSelectedInvoices(new Set());
    }
  }, [currentPage, filters, searchQuery, columnFilters]);

  // Fetch invoices from unified API
  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      // Build type filter - send comma-separated if multiple selected
      let typeFilter: string | undefined = filters.type;
      if (columnFilters.types.length > 0) {
        typeFilter = columnFilters.types.join(',');
      }

      // Build client filter - send comma-separated if multiple selected
      let clientFilter: string | undefined = filters.clientId?.toString();
      if (columnFilters.clients.length > 0) {
        clientFilter = columnFilters.clients.join(',');
      }

      // Build matter filter - send comma-separated if multiple selected
      let matterFilter: string | undefined = filters.matterId?.toString();
      if (columnFilters.matters.length > 0) {
        matterFilter = columnFilters.matters.join(',');
      }

      // Build status filter - send comma-separated if multiple selected
      let statusFilter: string | undefined = filters.status;
      if (columnFilters.statuses.length > 0) {
        statusFilter = columnFilters.statuses.join(',');
      }

      const params: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        search: searchQuery,
        from_date: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : undefined,
        to_date: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : undefined,
        ordering: '-issue_date,-invoice_number', // Sort by date descending (newest first), then by invoice number alphabetically
      };

      // Add filters as strings (including comma-separated values)
      if (typeFilter) params.type = typeFilter;
      if (clientFilter) params.client = clientFilter;
      if (matterFilter) params.matter = matterFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await UnifiedInvoiceAPI.getInvoices(params);

      setInvoices(response.results);
      setTotalInvoices(response.count);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch invoices',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch totals
  const fetchTotals = async () => {
    try {
      // Build type filter - send comma-separated if multiple selected
      let typeFilter: string | undefined = filters.type;
      if (columnFilters.types.length > 0) {
        typeFilter = columnFilters.types.join(',');
      }

      // Build client filter - send comma-separated if multiple selected
      let clientFilter: string | undefined = filters.clientId?.toString();
      if (columnFilters.clients.length > 0) {
        clientFilter = columnFilters.clients.join(',');
      }

      // Build matter filter - send comma-separated if multiple selected
      let matterFilter: string | undefined = filters.matterId?.toString();
      if (columnFilters.matters.length > 0) {
        matterFilter = columnFilters.matters.join(',');
      }

      // Build status filter - send comma-separated if multiple selected
      let statusFilter: string | undefined = filters.status;
      if (columnFilters.statuses.length > 0) {
        statusFilter = columnFilters.statuses.join(',');
      }

      const params: any = {
        search: searchQuery,
        from_date: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : undefined,
        to_date: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : undefined,
      };

      // Add filters as strings (including comma-separated values)
      if (typeFilter) params.type = typeFilter;
      if (clientFilter) params.client = clientFilter;
      if (matterFilter) params.matter = matterFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await UnifiedInvoiceAPI.getTotals(params);
      setTotals(response);
    } catch (error) {
      console.error('Failed to fetch totals:', error);
      setTotals({ total_sum: 0, paid_sum: 0, vat_amount_sum: 0, service_charge_sum: 0, costs: 0, count: 0 });
    }
  };

  // Handle column filter changes from table
  const handleColumnFiltersApplied = (appliedFilters: {
    types: Set<string>;
    clients: Set<number>;
    matters: Set<number>;
    statuses: Set<string>;
  }) => {
    setColumnFilters({
      types: Array.from(appliedFilters.types),
      clients: Array.from(appliedFilters.clients),
      matters: Array.from(appliedFilters.matters),
      statuses: Array.from(appliedFilters.statuses),
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Always show bulk actions when there are invoices (multi-select for all statuses)
  const showBulkActions = useMemo(() => {
    return invoices.length > 0;
  }, [invoices]);

  // Handle selecting/deselecting individual invoices
  const handleInvoiceSelection = (invoiceId: number, isSelected: boolean) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(invoiceId);
      } else {
        newSet.delete(invoiceId);
      }
      return newSet;
    });
  };

  // Handle selecting/deselecting all invoices
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedInvoices(new Set(invoices.map(invoice => invoice.id)));
    } else {
      setSelectedInvoices(new Set());
    }
  };

  // Handle data refresh after actions
  const handleDataRefresh = () => {
    fetchInvoices();
    fetchTotals();
    setSelectedInvoices(new Set());
  };

  // Handle send email for a single invoice
  const handleSendEmail = (invoice: UnifiedInvoice) => {
    setSelectedInvoice(invoice);
    setShowEmailDialog(true);
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice: UnifiedInvoice) => {
    setSelectedInvoice(invoice);
    setShowEditDialog(true);
  };

  // Handle apply to invoice (credit notes/down payments)
  const handleApplyToInvoice = (invoice: UnifiedInvoice) => {
    setSelectedInvoice(invoice);
    setShowApplyDialog(true);
  };

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Invoice management"
          description="All invoices, credit notes, and down payments in one place."
          actions={
            <>
              {/* Subtle entry point into the anomaly triage page. Kept here
                  rather than in the sidebar so users don't get greeted
                  with a warning icon every day. */}
              <Button asChild variant="ghost" size="sm" className="gap-2 text-muted-foreground">
                <Link to="/audit/anomalies">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="hidden sm:inline">Anomalies</span>
                </Link>
              </Button>
              <Button asChild variant="default">
                <Link to="/create-invoice">
                  <Plus className="h-4 w-4 mr-2" />
                  From timesheets
                </Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Quick create
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setCreateType('invoice'); setShowCreateDialog(true); }}>
                    <FileText className="h-4 w-4 mr-2" />
                    Standalone invoice
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setCreateType('credit_note'); setShowCreateDialog(true); }}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Credit note
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setCreateType('downpayment'); setShowCreateDialog(true); }}>
                    <Receipt className="h-4 w-4 mr-2" />
                    Down payment
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />
        <PageBody>

        {/* Filters and Search */}
        <Card>
          <CardHeader className="p-4 pb-3">
            <InvoiceFilters
              searchQuery={searchQuery}
              onSearchQueryChange={(query) => {
                setSearchQuery(query);
                setCurrentPage(1); // Reset to page 1 when search changes
              }}
            />
          </CardHeader>

          <CardContent className="p-4 pt-0">
            <InvoiceTable
              invoices={invoices}
              isLoading={isLoading}
              clientMap={clientMap}
              matterMap={matterMap}
              totals={totals}
              selectedInvoices={selectedInvoices}
              onInvoiceSelection={handleInvoiceSelection}
              onSelectAll={handleSelectAll}
              onSendEmail={handleSendEmail}
              onEditInvoice={handleEditInvoice}
              onApplyToInvoice={handleApplyToInvoice}
              onStatusChanged={handleDataRefresh}
              showBulkActions={showBulkActions}
              onBulkEmailClick={() => setShowBulkEmailDialog(true)}
              onMergeClick={() => setShowMergeDialog(true)}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalInvoices={totalInvoices}
              onPageChange={setCurrentPage}
              onFiltersApplied={handleColumnFiltersApplied}
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onDateFilterApply={(dateFrom, dateTo) => {
                setFilters({ ...filters, dateFrom, dateTo });
                setCurrentPage(1);
              }}
            />
          </CardContent>
        </Card>

        {/* Invoice Details Dialog */}
        {selectedInvoice && showDetailsDialog && (
          <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <InvoiceDetails 
                invoice={selectedInvoice as any} 
                onUpdate={handleDataRefresh}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Create Dialog */}
        <CreateInvoiceDialog
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          createType={createType}
          matters={matters}
          clients={clients}
          invoices={invoices}
          onInvoiceCreated={handleDataRefresh}
          defaultInvoiceDescription={firmSettings?.default_invoice_description}
        />

        {/* Send Email Dialog */}
        <SendEmailDialog
          isOpen={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          invoice={selectedInvoice}
          onEmailSent={handleDataRefresh}
        />

        {/* Edit Invoice Dialog */}
        <EditInvoiceDialog
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          invoice={selectedInvoice}
          onInvoiceUpdated={handleDataRefresh}
        />

        {/* Apply Credit Note / Down Payment Dialog */}
        <ApplyToInvoiceDialog
          isOpen={showApplyDialog}
          onClose={() => setShowApplyDialog(false)}
          invoice={selectedInvoice}
          onApplicationComplete={handleDataRefresh}
        />

        {/* Bulk Email Dialog */}
        <BulkEmailDialog
          isOpen={showBulkEmailDialog}
          onClose={() => setShowBulkEmailDialog(false)}
          selectedInvoices={selectedInvoices}
          invoices={invoices}
          clientMap={clientMap}
          onEmailsSent={handleDataRefresh}
        />

        {/* Merge Invoices Dialog */}
        <MergeInvoicesDialog
          isOpen={showMergeDialog}
          onClose={() => setShowMergeDialog(false)}
          selectedInvoices={selectedInvoices}
          invoices={invoices}
          clientMap={clientMap}
          matterMap={matterMap}
          onMergeComplete={handleDataRefresh}
        />
        </PageBody>
      </PageShell>
    </Layout>
  );
}